import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Play, Pause, Check, Sparkles, User, Globe2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export interface JarvisVoice {
  id: string;
  org_id: string | null;
  key: string;
  display_name: string;
  provider: string;
  voice_id: string;
  language: string | null;
  gender: string | null;
  description: string | null;
  is_cloned: boolean;
  active: boolean;
}

interface Props {
  orgId: string | null;
  value: string | null;
  onChange: (voiceKey: string, voiceId: string) => void;
  className?: string;
}

export function JarvisVoicePicker({ orgId, value, onChange, className }: Props) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const cacheRef = useRef<Map<string, string>>(new Map());

  const { data: voices = [], isLoading } = useQuery({
    queryKey: ["jarvis-voices", orgId],
    queryFn: async () => {
      let q = supabase
        .from("jarvis_voices")
        .select("*")
        .eq("active", true)
        .order("is_cloned", { ascending: false })
        .order("display_name");
      if (orgId) {
        q = q.or(`org_id.eq.${orgId},org_id.is.null`);
      } else {
        q = q.is("org_id", null);
      }
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as JarvisVoice[];
    },
  });

  const grouped = useMemo(() => {
    const cloned = voices.filter((v) => v.is_cloned);
    const global = voices.filter((v) => !v.is_cloned && v.org_id === null);
    return { cloned, global };
  }, [voices]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const playPreview = async (v: JarvisVoice) => {
    if (playingId === v.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    setLoadingId(v.id);
    try {
      let url = cacheRef.current.get(v.id);
      if (!url) {
        const { data, error } = await supabase.functions.invoke("jarvis-voice-preview", {
          body: { voice_id: v.voice_id, display_name: v.display_name, language: v.language ?? "pt-BR" },
        });
        if (error) throw new Error(error.message || "preview_failed");
        if (!data?.audio_base64) throw new Error("no_audio");
        url = `data:audio/mpeg;base64,${data.audio_base64}`;
        cacheRef.current.set(v.id, url);
      }
      audioRef.current?.pause();
      const a = new Audio(url);
      audioRef.current = a;
      a.onended = () => setPlayingId(null);
      a.onerror = () => { setPlayingId(null); toast.error("Falha no preview"); };
      await a.play();
      setPlayingId(v.id);
    } catch (e: any) {
      toast.error(`Preview falhou: ${e.message ?? e}`);
    } finally {
      setLoadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando vozes…
      </div>
    );
  }

  const renderVoice = (v: JarvisVoice) => {
    const active = value === v.key;
    return (
      <Card
        key={v.id}
        className={cn(
          "cursor-pointer transition-all hover:shadow-md",
          active && "ring-2 ring-primary border-primary",
        )}
        onClick={() => onChange(v.key, v.voice_id)}
      >
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-sm truncate">{v.display_name}</h4>
                {active && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
                {v.is_cloned && <Sparkles className="w-3 h-3 text-primary shrink-0" aria-label="Clonada" />}
              </div>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                {v.language && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">{v.language}</Badge>
                )}
                {v.gender && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize gap-1">
                    <User className="w-2.5 h-2.5" />{v.gender}
                  </Badge>
                )}
              </div>
            </div>
            <Button
              size="icon"
              variant="ghost"
              className="shrink-0 h-8 w-8"
              onClick={(e) => { e.stopPropagation(); void playPreview(v); }}
              disabled={loadingId === v.id}
              aria-label={playingId === v.id ? "Pausar" : "Tocar prévia"}
            >
              {loadingId === v.id ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : playingId === v.id ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </Button>
          </div>
          {v.description && (
            <p className="text-xs text-muted-foreground line-clamp-2">{v.description}</p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={cn("space-y-4", className)}>
      {grouped.cloned.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Suas vozes clonadas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {grouped.cloned.map(renderVoice)}
          </div>
        </div>
      )}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
          <Globe2 className="w-3.5 h-3.5" /> Catálogo global
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {grouped.global.map(renderVoice)}
        </div>
      </div>
    </div>
  );
}
